import { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { appWithTranslation } from 'next-i18next';
import { OverlayProvider } from 'overlay-kit';
import '@zep-us/zus/styles';
import '@/styles/globals.scss';
import { logger } from '@zep/core-logger';
import { AuthAppLoggerInitializer } from '@/components/AuthAppLoggerInitializer';
import { GlobalErrorBoundary } from '@/shared/components';

// design mode 전용 인스펙터. next/dynamic + ssr:false 로 별도 청크 분리해
// PRD 초기 번들에서 제외(렌더는 아래 NEXT_PUBLIC_STAGE 가드).
const DesignInspector = dynamic(() => import('@zep/internal').then(m => ({
  default: m.DesignInspector
})), {
  ssr: false
});
function AuthApp({
  Component,
  pageProps
}: AppProps) {
  const router = useRouter();
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        retry: failureCount => failureCount <= 3,
        refetchOnWindowFocus: false
      }
    }
  }));
  useEffect(() => {
    if (!router.isReady) return;
    logger.track('page_view', {
      location: router.pathname
    });
  }, [router.isReady, router.pathname]);
  return <>
      {process.env.NEXT_PUBLIC_STAGE !== 'PRD' && <DesignInspector />}
      <Head data-sentry-element="Head" data-sentry-source-file="_app.page.tsx">
        <title>ZEP Auth</title>
      </Head>
      <QueryClientProvider client={queryClient} data-sentry-element="QueryClientProvider" data-sentry-source-file="_app.page.tsx">
        <OverlayProvider data-sentry-element="OverlayProvider" data-sentry-source-file="_app.page.tsx">
          <AuthAppLoggerInitializer data-sentry-element="AuthAppLoggerInitializer" data-sentry-source-file="_app.page.tsx" />
          <GlobalErrorBoundary data-sentry-element="GlobalErrorBoundary" data-sentry-source-file="_app.page.tsx">
            <Component {...pageProps} data-sentry-element="Component" data-sentry-source-file="_app.page.tsx" />
          </GlobalErrorBoundary>
        </OverlayProvider>
      </QueryClientProvider>
    </>;
}
export default appWithTranslation(AuthApp);